import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getInvitationByToken, acceptInvitation } from '@/services/invitations';
import type { Invitation } from '@/lib/apiClient';
import './InviteAccept.css';

export default function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchInvitation() {
      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const inv = await getInvitationByToken(token);
        if (!inv) {
          setError('Invitation not found or has expired');
        } else if (inv.status === 'accepted') {
          setError('This invitation has already been accepted');
        } else if (inv.status === 'expired') {
          setError('This invitation has expired');
        } else if (!inv.organization) {
          setError('Organization not found');
        } else {
          setInvitation(inv);
        }
      } catch (err) {
        console.error('Failed to fetch invitation:', err);
        setError('Failed to load invitation');
      } finally {
        setLoading(false);
      }
    }

    fetchInvitation();
  }, [token]);

  const handleAccept = async () => {
    if (!user || !token) return;

    setAccepting(true);
    setError(null);

    try {
      const result = await acceptInvitation(token, user.id);

      if (result.success) {
        if (invitation?.memberType === 'candidate') {
          navigate(`/start?org=${result.organizationId}`);
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(result.error || 'Failed to accept invitation');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleAuthRedirect = (path: '/login' | '/signup') => {
    if (token) {
      localStorage.setItem('pending_invite_token', token);
    }
    window.location.href = path;
  };

  if (loading || authLoading) {
    return (
      <div className="invite-page">
        <div className="invite-container">
          <div className="loading-spinner" />
          <p>Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invite-page">
        <div className="invite-container">
          <div className="invite-card error">
            <div className="error-icon">!</div>
            <h1>Invitation Error</h1>
            <p>{error}</p>
            <Link to="/" className="btn btn-primary">
              Go to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="invite-page">
        <div className="invite-container">
          <div className="invite-card error">
            <div className="error-icon">?</div>
            <h1>Invitation Not Found</h1>
            <p>This invitation link is invalid or has expired.</p>
            <Link to="/" className="btn btn-primary">
              Go to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isCandidate = invitation.memberType === 'candidate';
  const emailMismatch = user && invitation.email && user.email?.toLowerCase() !== invitation.email.toLowerCase();

  const maskEmail = (email: string) => {
    const [local, domain] = email.split('@');
    if (local.length <= 2) return local[0] + '***@' + domain;
    return local[0] + '***' + local.slice(-2) + '@' + domain;
  };

  return (
    <div className="invite-page">
      <div className="invite-container">
        <div className="invite-card">
          <div className="invite-badge">{isCandidate ? 'Assessment Invite' : 'Team Invite'}</div>

          <h1>You're Invited!</h1>

          <p className="invite-org">
            <strong>{invitation.organization?.name}</strong> has invited you to
            {isCandidate
              ? ' complete the E.Q.U.I.P. 360 Leadership Assessment.'
              : ' join their team on E.Q.U.I.P. 360.'}
          </p>

          {isCandidate && (
            <div className="invite-details">
              <h3>About the Assessment</h3>
              <ul>
                <li>20 scenario-based questions</li>
                <li>Measures emotional intelligence &amp; leadership potential</li>
                <li>Takes approximately 15-20 minutes</li>
                <li>Receive your Leadership Identity and personalized insights</li>
              </ul>
            </div>
          )}

          {user && emailMismatch ? (
            <div className="invite-actions">
              <div className="email-mismatch-warning">
                <p>This invitation was sent to <strong>{maskEmail(invitation.email)}</strong>, but you are signed in as <strong>{user.email}</strong>.</p>
                <p>Please sign out and sign in with the correct email to accept this invitation.</p>
              </div>
              <button
                className="btn btn-secondary btn-lg"
                onClick={() => {
                  if (token) {
                    localStorage.setItem('pending_invite_token', token);
                  }
                  window.location.href = '/api/auth/logout';
                }}
              >
                Sign Out & Switch Account
              </button>
            </div>
          ) : user ? (
            <div className="invite-actions">
              <button
                className="btn btn-primary btn-lg"
                onClick={handleAccept}
                disabled={accepting}
              >
                {accepting
                  ? 'Accepting...'
                  : isCandidate
                    ? 'Accept & Start Assessment'
                    : 'Join Team'}
              </button>
              {isCandidate ? (
                <p className="accept-note">
                  You'll be directed to a 15-20 minute leadership assessment after accepting.
                </p>
              ) : (
                <p className="accept-note">
                  You'll get access to the team dashboard and shared leadership insights.
                </p>
              )}
              <p className="logged-in-as">
                Signed in as <strong>{user.email}</strong>
              </p>
            </div>
          ) : (
            <div className="invite-actions">
              <p className="auth-prompt">Sign in or create an account to accept this invitation:</p>
              <div className="auth-buttons">
                <button className="btn btn-primary btn-lg" onClick={() => handleAuthRedirect('/login')}>
                  Sign In
                </button>
                <button className="btn btn-secondary btn-lg" onClick={() => handleAuthRedirect('/signup')}>
                  Create Account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
